import type { CategoryMeta, CategorySlug } from "./types";

export const CATEGORIES: CategoryMeta[] = [
  {
    slug: "property",
    title: "Property & Real Estate",
    shortTitle: "Property",
    description:
      "Stamp duty, registration charges, circle rates, property tax, carpet area, rental yield, and every other calculator you need for buying, selling, or investing in Indian real estate.",
    icon: "Building2",
    color: "text-blue-600",
    gradient: "from-blue-500 to-blue-700",
  },
  {
    slug: "loans",
    title: "Loans & EMI",
    shortTitle: "Loans",
    description:
      "Calculate EMI for home loans, car loans, personal loans, and more. Compare rates, check prepayment savings, and plan your borrowing with India-specific tools.",
    icon: "Landmark",
    color: "text-emerald-600",
    gradient: "from-emerald-500 to-emerald-700",
  },
  {
    slug: "tax",
    title: "Tax Calculators",
    shortTitle: "Tax",
    description:
      "Income tax, TDS, GST, HRA exemption, capital gains, advance tax — all updated for the latest Indian tax slabs and rules.",
    icon: "Receipt",
    color: "text-red-600",
    gradient: "from-red-500 to-red-700",
  },
  {
    slug: "investment",
    title: "Investment Calculators",
    shortTitle: "Investment",
    description:
      "SIP, lumpsum, CAGR, XIRR, mutual fund returns, ELSS, NPS — plan your investments with precise, India-focused calculators.",
    icon: "TrendingUp",
    color: "text-violet-600",
    gradient: "from-violet-500 to-violet-700",
  },
  {
    slug: "retirement",
    title: "Retirement & Pension",
    shortTitle: "Retirement",
    description:
      "EPF, gratuity, NPS, pension, FIRE — calculate your retirement corpus and monthly pension under Indian regulations.",
    icon: "Armchair",
    color: "text-amber-600",
    gradient: "from-amber-500 to-amber-700",
  },
  {
    slug: "salary",
    title: "Salary & HR",
    shortTitle: "Salary",
    description:
      "CTC to in-hand, salary hike, bonus, leave encashment, PF contribution, professional tax — all tailored for Indian payrolls.",
    icon: "BadgeIndianRupee",
    color: "text-teal-600",
    gradient: "from-teal-500 to-teal-700",
  },
  {
    slug: "business",
    title: "Business & GST",
    shortTitle: "Business",
    description:
      "GST calculations, profit margin, ROI, breakeven, working capital, depreciation — tools for Indian entrepreneurs and businesses.",
    icon: "Briefcase",
    color: "text-indigo-600",
    gradient: "from-indigo-500 to-indigo-700",
  },
  {
    slug: "rent",
    title: "Rent & Lease",
    shortTitle: "Rent",
    description:
      "Rent agreement costs, rental yield, HRA calculations, rent vs buy comparison — everything for tenants, landlords, and investors.",
    icon: "Home",
    color: "text-orange-600",
    gradient: "from-orange-500 to-orange-700",
  },
  {
    slug: "insurance",
    title: "Insurance",
    shortTitle: "Insurance",
    description:
      "Life insurance, health insurance, car insurance, term plan — estimate premiums and coverage needs for Indian policies.",
    icon: "ShieldCheck",
    color: "text-cyan-600",
    gradient: "from-cyan-500 to-cyan-700",
  },
  {
    slug: "legal",
    title: "Legal & Compliance",
    shortTitle: "Legal",
    description:
      "Court fees, advocate fees, company registration, trademark costs, FSSAI, IEC — legal compliance cost calculators for India.",
    icon: "Scale",
    color: "text-slate-600",
    gradient: "from-slate-500 to-slate-700",
  },
  {
    slug: "savings",
    title: "Savings & Deposits",
    shortTitle: "Savings",
    description:
      "FD, RD, PPF, NSC, KVP, SCSS — calculate maturity amounts and interest for Indian savings instruments.",
    icon: "PiggyBank",
    color: "text-pink-600",
    gradient: "from-pink-500 to-pink-700",
  },
  {
    slug: "personal-finance",
    title: "Personal Finance",
    shortTitle: "Personal",
    description:
      "Budget planning, emergency fund, net worth, debt payoff, inflation, marriage budget — manage your personal finances in India.",
    icon: "Wallet",
    color: "text-lime-600",
    gradient: "from-lime-500 to-lime-700",
  },
  {
    slug: "nri",
    title: "NRI Calculators",
    shortTitle: "NRI",
    description:
      "NRE/NRO FD, FCNR deposits, NRI home loans, TDS on NRI property, repatriation — tailored for Non-Resident Indians.",
    icon: "Globe",
    color: "text-sky-600",
    gradient: "from-sky-500 to-sky-700",
  },
  {
    slug: "education",
    title: "Education & Student",
    shortTitle: "Education",
    description:
      "Education loan EMI, study abroad cost, child education planning, scholarship eligibility — plan education expenses in India.",
    icon: "GraduationCap",
    color: "text-fuchsia-600",
    gradient: "from-fuchsia-500 to-fuchsia-700",
  },
  {
    slug: "vehicle",
    title: "Vehicle & Transport",
    shortTitle: "Vehicle",
    description:
      "Car loan EMI, road tax, RTO fees, insurance, fuel cost, EV vs petrol comparison — all vehicle-related calculators for India.",
    icon: "Car",
    color: "text-rose-600",
    gradient: "from-rose-500 to-rose-700",
  },
  {
    slug: "gold",
    title: "Gold & Commodities",
    shortTitle: "Gold",
    description:
      "Gold price, gold loan, purity calculator, making charges, SGB returns, silver price — precious metals calculators for India.",
    icon: "Gem",
    color: "text-yellow-600",
    gradient: "from-yellow-500 to-yellow-600",
  },
];

export const CATEGORY_MAP: Record<CategorySlug, CategoryMeta> = Object.fromEntries(
  CATEGORIES.map((c) => [c.slug, c])
) as Record<CategorySlug, CategoryMeta>;

export function getCategoryMeta(slug: string): CategoryMeta | undefined {
  return CATEGORY_MAP[slug as CategorySlug];
}
